import api from './api';
import { env } from '../utils/env';

/**
 * Interface de Backup
 */
export interface Backup {
  id: number;
  tipo: 'manual' | 'automatico' | 'upload';
  status: 'processando' | 'sucesso' | 'erro';
  data_criacao: string;
  nome_arquivo: string;
  tamanho_bytes: number | null;
  hash_md5: string | null;
  tabelas_incluidas: string | null;
  arquivos_incluidos: boolean;
  usuario_id: number | null;
  observacoes: string | null;
  erro_mensagem: string | null;
  createdAt: string;
  updatedAt: string;
  usuario?: {
    id: number;
    nome: string;
    usuario: string;
  };
}

export interface BackupListResponse {
  success: boolean;
  data: Backup[];
}

export interface CreateBackupData {
  observacoes?: string;
}

export interface BackupConfiguracoes {
  automaticoEnabled: boolean;
  retencaoDias: number;
  // Mega.io
  megaEnabled: boolean;
  megaEmail: string;
  megaPassword: string;
  // FTP
  ftpEnabled: boolean;
  ftpHost: string;
  ftpPort: number;
  ftpUsername: string;
  ftpPassword: string;
  ftpSecure: boolean;
}

class BackupService {
  /**
   * Listar todos os backups
   */
  async listarBackups(): Promise<Backup[]> {
    const response: any = await api.get('/backup');
    return response.data || [];
  }

  /**
   * Obter backup por ID
   */
  async obterBackup(id: number): Promise<Backup> {
    const response: any = await api.get(`/backup/${id}`);
    return response.data;
  }

  /**
   * Criar backup manual
   */
  async criarBackupManual(data?: CreateBackupData): Promise<{ message: string; backup_id: number }> {
    const response: any = await api.post('/backup/create', data);
    return response;
  }

  /**
   * Download de backup
   */
  async downloadBackup(id: number, nomeArquivo: string): Promise<void> {
    try {
      // Fazer requisição com responseType blob (igual ao clienteDocumentoService)
      const response = await api.get(`/backup/${id}/download`, {
        responseType: 'blob',
      });

      // Se response já for Blob, retorne ele diretamente
      const blob = response instanceof Blob ? response : response.data;

      // Criar link de download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;

      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpar URL temporária
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Erro ao fazer download:', error);
      throw error;
    }
  }

  /**
   * Deletar backup
   */
  async deletarBackup(id: number): Promise<{ message: string }> {
    const response: any = await api.delete(`/backup/${id}`);
    return response;
  }

  /**
   * Upload de backup existente
   */
  async uploadBackup(file: File, observacoes?: string): Promise<{ message: string; backup_id: number }> {
    const formData = new FormData();
    formData.append('file', file);
    if (observacoes) {
      formData.append('observacoes', observacoes);
    }

    const response: any = await api.post('/backup/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  }

  /**
   * Restaurar backup
   */
  async restaurarBackup(id: number): Promise<{ message: string }> {
    const response: any = await api.post(`/backup/${id}/restore`);
    return response;
  }

  /**
   * Obter configurações de backup
   */
  async obterConfiguracoes(): Promise<BackupConfiguracoes> {
    const response = await api.get<BackupConfiguracoes>('/backup/configuracoes/obter');
    return response.data;
  }

  /**
   * Formatar tamanho do arquivo
   */
  formatarTamanho(bytes: number | null): string {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Formatar duração
   */
  formatarDuracao(segundos: number | null): string {
    if (!segundos || segundos === 0) return '-';

    if (segundos < 60) {
      return `${segundos}s`;
    } else if (segundos < 3600) {
      const minutos = Math.floor(segundos / 60);
      const segs = segundos % 60;
      return segs > 0 ? `${minutos}m ${segs}s` : `${minutos}m`;
    } else {
      const horas = Math.floor(segundos / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);
      return minutos > 0 ? `${horas}h ${minutos}m` : `${horas}h`;
    }
  }

  /**
   * Obter cor do status
   */
  obterCorStatus(status: string): 'success' | 'error' | 'warning' | 'info' {
    switch (status) {
      case 'sucesso':
        return 'success';
      case 'erro':
        return 'error';
      case 'processando':
        return 'info';
      default:
        return 'warning';
    }
  }

  /**
   * Testar conexão com Mega.io
   */
  async testMegaConnection(email: string, password: string): Promise<{ success: boolean; message: string }> {
    const response: any = await api.post('/backup/mega/test', { email, password });
    return response;
  }


  /**
   * Testar conexão com FTP
   */
  async testFTPConnection(host: string, port: number, username: string, password: string, secure: boolean): Promise<{ success: boolean; message: string }> {
    const response: any = await api.post('/backup/ftp/test', {
      host,
      port,
      username,
      password,
      secure
    });
    return response;
  }

  /**
   * Salvar configurações
   */
  async salvarConfiguracoes(config: {
    automaticoEnabled?: boolean;
    retencaoDias?: number;
    megaEnabled?: boolean;
    megaEmail?: string;
    megaPassword?: string;
    ftpEnabled?: boolean;
    ftpHost?: string;
    ftpPort?: number;
    ftpUsername?: string;
    ftpPassword?: string;
    ftpSecure?: boolean;
  }): Promise<{ message: string }> {
    const response: any = await api.post('/backup/configuracoes/salvar', config);
    return response;
  }
}

export const backupService = new BackupService();
export default backupService;

